import { push } from 'react-router-redux';
import type { RemoteData } from '../async-actions';
import { createFetchAction } from '../async-actions';
import type { ICustomer } from '../domain';
import type { IDataLayer } from '../helpers/GoogleTagManager';
import { getDataLayer, setDataLayer } from '../helpers/GoogleTagManager';
import { key } from '../helpers/LocalStorage';
import type { ISession } from '../helpers/Session';
import { getSession, setSession } from '../helpers/Session';
import * as V2Hub from '../remote/hubs/Web_Controllers_V2_CustomersController';

export interface ICustomerState {
	customers: RemoteData<ICustomer[], string[]>;
	currentCustomer: RemoteData<ICustomer, string[]>;
}

const initialState: ICustomerState = {
	customers: { type: 'initial' },
	currentCustomer: { type: 'initial' },
};

const handleCustomerListAction = defaultRemoteDataReducer<
	LOADED_CUSTOMERS_PAGE_PENDING,
	LOADED_CUSTOMERS_PAGE_FAILURE,
	LOADED_CUSTOMERS_PAGE_SUCCESS
>(
	'@ca:LOADED_CUSTOMERS_PAGE_PENDING',
	'@ca:LOADED_CUSTOMERS_PAGE_FAILURE',
	'@ca:LOADED_CUSTOMERS_PAGE_SUCCESS'
);

export function customersReducer(
	state = initialState,
	action: CustomerAction
): ICustomerState {
	switch (action.type) {
		case '@ca:LOADED_CUSTOMERS_PAGE_PENDING':
		case '@ca:LOADED_CUSTOMERS_PAGE_FAILURE':
		case '@ca:LOADED_CUSTOMERS_PAGE_SUCCESS': {
			return {
				...state,
				customers: handleCustomerListAction(action),
			};
		}
		case '@ca:SELECTED_CUSTOMER_PENDING':
		case '@ca:SELECTED_CUSTOMER_FAILURE': {
			return {
				...state,
			};
		}
		case '@ca:SELECTED_CUSTOMER_SUCCESS': {
			return {
				...state,
				currentCustomer: {
					type: 'success',
					data: action.payload,
				},
			};
		}
	}
	return state;
}

function defaultRemoteDataReducer<
	TPending extends {
		type: TPending['type'];
	},
	TFailure extends {
		type: TFailure['type'];
		errors: TFailure['errors'];
	},
	TSuccess extends {
		type: TSuccess['type'];
		payload: TSuccess['payload'];
	}
>(
	pending: TPending['type'],
	failure: TFailure['type'],
	success: TSuccess['type']
) {
	const f = (
		action: TPending | TSuccess | TFailure
	): RemoteData<TSuccess['payload'], TFailure['errors']> => {
		if (action.type === pending) {
			return {
				type: 'loading',
			};
		}

		if (action.type === failure) {
			// TODO make compiler understand this
			const failAction = action as TFailure;
			return {
				type: 'failure',
				err: failAction.errors,
			};
		}

		if (action.type === success) {
			// TODO make compiler understand this
			const successAction = action as TSuccess;
			return {
				type: 'success',
				data: successAction.payload,
			};
		}

		throw new Error(`unknown action: ${JSON.stringify(action, null, 4)}`);
	};
	return f;
}

export type CustomerAction =
	| LOADED_CUSTOMERS_PAGE_FAILURE
	| LOADED_CUSTOMERS_PAGE_PENDING
	| LOADED_CUSTOMERS_PAGE_SUCCESS
	| SELECTED_CUSTOMER_FAILURE
	| SELECTED_CUSTOMER_PENDING
	| SELECTED_CUSTOMER_SUCCESS;

export interface LOADED_CUSTOMERS_PAGE_PENDING {
	type: '@ca:LOADED_CUSTOMERS_PAGE_PENDING';
}

export interface LOADED_CUSTOMERS_PAGE_FAILURE {
	type: '@ca:LOADED_CUSTOMERS_PAGE_FAILURE';
	errors: string[];
}

export interface LOADED_CUSTOMERS_PAGE_SUCCESS {
	type: '@ca:LOADED_CUSTOMERS_PAGE_SUCCESS';
	payload: V2Hub.ICustomer[];
}

export const loadedCustomersPage = createFetchAction<
	{},
	LOADED_CUSTOMERS_PAGE_PENDING,
	LOADED_CUSTOMERS_PAGE_FAILURE,
	LOADED_CUSTOMERS_PAGE_SUCCESS
>(
	'@ca:LOADED_CUSTOMERS_PAGE_PENDING',
	'@ca:LOADED_CUSTOMERS_PAGE_FAILURE',
	'@ca:LOADED_CUSTOMERS_PAGE_SUCCESS',
	() => {
		return V2Hub.GetCustomersAssignedToUser();
	}
);

export interface SELECTED_CUSTOMER_PENDING {
	type: '@ca:SELECTED_CUSTOMER_PENDING';
}

export interface SELECTED_CUSTOMER_FAILURE {
	type: '@ca:SELECTED_CUSTOMER_FAILURE';
	errors: string[];
}

export interface SELECTED_CUSTOMER_SUCCESS {
	type: '@ca:SELECTED_CUSTOMER_SUCCESS';
	payload: V2Hub.ICustomer;
}

export const selectedCustomer = createFetchAction<
	number,
	SELECTED_CUSTOMER_PENDING,
	SELECTED_CUSTOMER_FAILURE,
	SELECTED_CUSTOMER_SUCCESS
>(
	'@ca:SELECTED_CUSTOMER_PENDING',
	'@ca:SELECTED_CUSTOMER_FAILURE',
	'@ca:SELECTED_CUSTOMER_SUCCESS',
	(args, dispatch) => {
		const promise = V2Hub.SwitchCustomerContext(args).then((result) => {
			if (result.ok) {
				const payload = result.value;
				const existingSession = getSession();
				const existingDataLayer = getDataLayer();

				const sessionBits = getSelectedCustomerSessionBits(
					existingSession,
					existingDataLayer,
					payload
				);

				setSession(sessionBits.session);
				setDataLayer(sessionBits.dataLayer);
				for (const prop in sessionBits.localStorage) {
					window.localStorage.setItem(prop, sessionBits.localStorage[prop]);
				}

				dispatch(push('/'));
			}
			return result;
		});
		return promise;
	}
);

// TODO(chrisw): Test this function!
export function getSelectedCustomerSessionBits(
	existingSession: ISession,
	existingDataLayer: IDataLayer,
	payload: ICustomer
) {
	return {
		session: {
			...existingSession,
			customer: payload,

			// TODO(chrisw): deprecate 'em
			customerName: payload.name,
			customerId: +payload.id,
			customerAttributes: payload.attributes.map((x) => Number(x)),
		},
		localStorage: {
			[key.customerName]: payload.name,
			[key.customerId]: payload.id.toString(),
			[key.customerAttributes]: JSON.stringify(payload.attributes),
		},
		dataLayer: {
			...existingDataLayer,
			user_corp: payload.id,
		},
	};
}
